/**
 * Translation paths for business errors (HTTP 422 / 409) coming from the `manufactory` service.
 *
 * `ErrorHandlerService.handleBusinessErrors` builds the key as `${translationPath}.${camelCase(code)}`,
 * where `code` is the backend exception's type name with the `Exception` suffix removed
 * (e.g. `NoTaskAssignedException` -> `noTaskAssigned`).
 *
 * A missing translation is not a failure - `common.entity.unexpectedError` is shown instead.
 *
 * ▶ **Some codes are generic and are disambiguated only by their path.** The shared
 * `aggregateRootAlreadyExists` and `aggregateRootCannotBeCreated` are raised by several different
 * aggregates, so the wording that makes them meaningful lives per path - "A profession with this number
 * already exists." under `professionsList`, "An active cart already exists for the user." under `cart`,
 * and so on. That works only because each path today covers exactly one create.
 * **If you add a second create to an existing path, these two codes stop being able to tell the two
 * apart** - split the path, or word the message so it is true for both.
 */

/** Work timer (`shared/modules/time-tracker`). */
export const TIME_TRACKER_ERROR_TRANSLATION_PATH = 'common.timeTracker.notification.error';

/** Daily plan detail (`shared/modules/daily-plan-detail-drawer`). */
export const DAILY_PLAN_DETAIL_ERROR_TRANSLATION_PATH = 'common.dailyPlanDetail.notification.error';

/** Delivery notes (`features/manufactory/delivery-notes`). */
export const DELIVERY_NOTES_ERROR_TRANSLATION_PATH = 'manufactory.deliveryNotes.notification.error';

/** Profession groups (`features/manufactory/administration/profession-groups-list`). */
export const PROFESSION_GROUPS_ERROR_TRANSLATION_PATH = 'manufactory.professionGroupsList.notification.error';

/** Profession types (`features/manufactory/administration/list-books/profession-types-list`). */
export const PROFESSION_TYPES_ERROR_TRANSLATION_PATH = 'manufactory.professionTypesList.notification.error';

/** Professions (`features/manufactory/administration/professions-list`). */
export const PROFESSIONS_ERROR_TRANSLATION_PATH = 'manufactory.professionsList.notification.error';

/** Cart (`features/logistics/cart-list`, plus the "add to basket" actions on material sheets). */
export const CART_ERROR_TRANSLATION_PATH = 'logistics.cart.notification.error';

/**
 * Cart scanner (`features/logistics/cart-scanner`).
 *
 * The anonymous cart sends the same commands as the normal cart (`AddCartItemCommand`,
 * `SetCartItemQuantityCommand`, `RemoveCartItemCommand`), so it raises the same item codes - but it never
 * creates a cart, and therefore can never raise `cartAlreadyExists`.
 */
export const CART_SCANNER_ERROR_TRANSLATION_PATH = 'logistics.cartScanner.notification.error';

/** Assigning a workshop referent to a batch (`daily-plan-list/daily-plan-referent-drawer`). */
export const BATCH_REFERENT_ERROR_TRANSLATION_PATH = 'manufactory.drawers.referentBatches.notification.error';

/** Material sheets and warehouse movements (`features/logistics`). */
export const MATERIAL_SHEETS_ERROR_TRANSLATION_PATH = 'logistics.materialSheetsGroup.notification.error';

/** Requests for material (`features/manufactory/requests-for-material`). */
export const REQUESTS_FOR_MATERIAL_ERROR_TRANSLATION_PATH = 'manufactory.requestForMaterial.notification.error';

/**
 * Shift schedules (`features/administration/shift-schedule`, editor drawer).
 *
 * The only business code this area can raise is the shared `aggregateRootCannotBeCreated` -
 * `ShiftSchedule` declares no exception of its own.
 */
export const SHIFT_SCHEDULE_ERROR_TRANSLATION_PATH = 'administration.shiftSchedule.notification.error';

/** Calendar (`features/administration/calendar`). */
export const CALENDAR_ERROR_TRANSLATION_PATH = 'administration.calendar.notification.error';
